import { LayoutTemplate } from "../../Templates/LayoutTemplate";
import { MesesDoAnoEnum } from "../../../enums/mesesDoAno";
import { card, num, pct, pctLabel } from "../../Templates/GeneralTemplate/ui";
import { useProductivityServiceByClient } from "./useProductivityServiceByClient";

const fieldLabel =
  "text-xs font-semibold uppercase tracking-wider text-[#6B6B66]";
const selectCls =
  "h-11 w-full appearance-none rounded-[10px] border border-[#DAD9D4] bg-white pl-3.5 pr-9 text-sm disabled:border-[#E7E6E2] disabled:bg-[#F7F7F5] disabled:text-[#6B6B66]";

const TIPOS = [
  { label: "Particular", qtd: "qtdParticular", per: "perParticular", color: "#1D1D1B", dark: "#FFFFFF" },
  { label: "Despachante", qtd: "qtdDespachante", per: "perDespachante", color: "#E84E1B", dark: "#E84E1B" },
  { label: "Concessionária", qtd: "qtdConcessionaria", per: "perConcessionaria", color: "#F4A988", dark: "#F4A988" },
  { label: "Lojista", qtd: "qtdLojista", per: "perLojista", color: "#A3A29C", dark: "#8A8A84" },
] as const;

// "DESPACHANTE" → "Despachante"; valores já formatados passam intactos
const tipoLabel = (tipo?: string) =>
  tipo && tipo === tipo.toUpperCase()
    ? tipo.charAt(0) + tipo.slice(1).toLowerCase()
    : tipo || "---";
const tipoColor = (tipo?: string) =>
  (tipo &&
    TIPOS.find((t) =>
      t.label.toLowerCase().startsWith(tipo.toLowerCase().slice(0, 6))
    )?.color) ||
  "#DAD9D4";

const Chevron = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6B6B66"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="pointer-events-none absolute right-3 top-3.5"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const ProductivityServiceByClient = () => {
  const {
    mothOptions,
    yearOptions,
    filter,
    appliedFilter,
    setFilter,
    empresasOptions,
    lojasOptions,
    handleFilter,
    producao,
    handleDownDoc,
  } = useProductivityServiceByClient();

  const unidades = producao.producaoUnidadeTipoClienteList || [];
  const rowTotal = (row: (typeof unidades)[number]) =>
    TIPOS.reduce((acc, t) => acc + (row[t.qtd] || 0), 0);
  const sums = TIPOS.map((t) =>
    unidades.reduce((acc, row) => acc + (row[t.qtd] || 0), 0)
  );
  const total = sums.reduce((a, b) => a + b, 0);
  const ranking = (producao.clienteRankingList || [])
    .slice()
    .sort((a, b) => b.quantidade_vistorias - a.quantidade_vistorias);
  const topQtd = ranking[0]?.quantidade_vistorias || 0;

  return (
    <LayoutTemplate title="Relatório de Produção por Tipo de Cliente">
      <div className="flex flex-col gap-5 text-[#1D1D1B]">
        <span className="text-sm text-[#6B6B66]">
          {appliedFilter.mes
            ? `${MesesDoAnoEnum[appliedFilter.mes]} de `
            : "Ano inteiro · "}
          {appliedFilter.ano} · {appliedFilter.empresa || "Todas as empresas"}
          {appliedFilter.cidade && ` · ${appliedFilter.cidade}`}
        </span>

        {/* Filtros */}
        <form
          onSubmit={handleFilter}
          className={`${card} flex flex-wrap items-end gap-5 py-4`}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ptc-mes" className={fieldLabel}>
              Mês
            </label>
            <div className="relative w-[180px]">
              <select
                id="ptc-mes"
                className={selectCls}
                value={filter.mes || ""}
                onChange={(e) =>
                  setFilter((prev) => ({
                    ...prev,
                    mes: e.target.value ? +e.target.value : null,
                  }))
                }
              >
                <option value="">Ano inteiro</option>
                {mothOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="ptc-ano" className={fieldLabel}>
              Ano
            </label>
            <div className="relative w-[120px]">
              <select
                id="ptc-ano"
                className={selectCls}
                value={filter.ano}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, ano: +e.target.value }))
                }
              >
                {yearOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="flex flex-[1_1_360px] flex-col gap-1.5">
            <span className={fieldLabel}>Empresa</span>
            <div className="flex flex-wrap gap-2">
              {[{ label: "Todas", value: null }, ...empresasOptions].map(
                (opt) => {
                  const active = (filter.empresa || null) === opt.value;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        setFilter((prev) => ({ ...prev, empresa: opt.value }))
                      }
                      className={`h-11 rounded-full border px-4 text-[13px] ${
                        active
                          ? "border-[#1D1D1B] bg-[#1D1D1B] font-semibold text-white"
                          : "border-[#DAD9D4] bg-white font-medium"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                }
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="ptc-loja" className={fieldLabel}>
              Loja
            </label>
            <div className="relative w-[220px]">
              <select
                id="ptc-loja"
                className={selectCls}
                disabled={!filter.empresa}
                value={filter.cidade || ""}
                onChange={(e) =>
                  setFilter((prev) => ({
                    ...prev,
                    cidade: e.target.value || null,
                  }))
                }
              >
                <option value="">
                  {filter.empresa ? "Todas as lojas" : "Selecione uma empresa"}
                </option>
                {lojasOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <button
            type="submit"
            className="h-11 rounded-[10px] bg-[#E84E1B] px-6 text-sm font-semibold text-white hover:bg-[#cf4316]"
          >
            Aplicar filtros
          </button>
        </form>

        {/* KPIs */}
        <section
          aria-label="Vistorias por tipo de cliente"
          className="flex flex-wrap gap-4"
        >
          <div className="flex flex-[1.4_1_260px] flex-col gap-3.5 rounded-2xl bg-[#1D1D1B] p-5 text-white">
            <span className="text-[13px] font-medium text-[#C9C8C2]">
              Total de vistorias
            </span>
            <span className="text-[40px] font-bold leading-none tabular-nums tracking-tight">
              {num(total)}
            </span>
            <div className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-[#3C3C3B]">
              {TIPOS.map((t, i) => (
                <span
                  key={t.label}
                  style={{ width: `${pct(sums[i], total)}%`, background: t.dark }}
                />
              ))}
            </div>
            <span className="text-[13px] text-[#C9C8C2]">
              em {unidades.length} unidades
            </span>
          </div>
          {TIPOS.map((t, i) => (
            <div
              key={t.label}
              className={`${card} flex flex-[1_1_180px] flex-col gap-3.5`}
            >
              <span className="inline-flex items-center gap-2 text-[13px] font-medium text-[#6B6B66]">
                <span
                  className="h-2.5 w-2.5 rounded-[3px]"
                  style={{ background: t.color }}
                />
                {t.label}
              </span>
              <span className="text-[32px] font-bold tabular-nums tracking-tight">
                {num(sums[i])}
              </span>
              <span className="self-start rounded-full bg-[#F1F0EC] px-2 py-0.5 text-xs font-semibold tabular-nums text-[#4A4A46]">
                {pctLabel(pct(sums[i], total))} do total
              </span>
            </div>
          ))}
        </section>

        {/* Tabela por unidade */}
        <section className="flex flex-col overflow-hidden rounded-2xl border border-[#E7E6E2] bg-white">
          <div className="flex items-baseline gap-2.5 px-5 py-4">
            <h2 className="text-base font-semibold">Produção por unidade</h2>
            <span className="text-[13px] text-[#6B6B66]">
              {unidades.length} unidades
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] border-collapse text-sm tabular-nums [&_td]:whitespace-nowrap [&_td]:px-3.5 [&_td]:py-3 [&_th]:whitespace-nowrap [&_th]:px-3.5 [&_th]:py-3">
              <thead>
                <tr className="bg-[#F7F7F5] text-right text-xs uppercase tracking-wider text-[#6B6B66] [&_th]:font-semibold">
                  <th className="text-left">Unidade</th>
                  <th className="text-left">Mix</th>
                  {TIPOS.map((t) => (
                    <th key={t.label}>
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="h-2 w-2 rounded-sm"
                          style={{ background: t.color }}
                        />
                        {t.label}
                      </span>
                    </th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {unidades.map((row) => {
                  const rt = rowTotal(row);
                  return (
                    <tr
                      key={`${row.empresa}-${row.unidade}`}
                      className="border-t border-[#EEEDE9] text-right hover:bg-[#FAFAF8]"
                    >
                      <td className="text-left">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-semibold">
                            {row.unidade || "---"}
                          </span>
                          <span className="text-xs text-[#6B6B66]">
                            {row.empresa || "---"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="flex h-2 w-28 gap-px overflow-hidden rounded-full bg-[#F1F0EC]">
                          {TIPOS.map((t) => (
                            <span
                              key={t.label}
                              style={{
                                width: `${pct(row[t.qtd], rt)}%`,
                                background: t.color,
                              }}
                            />
                          ))}
                        </div>
                      </td>
                      {TIPOS.map((t) => (
                        <td key={t.label}>
                          {num(row[t.qtd])}{" "}
                          <span className="inline-block w-[52px] text-xs text-[#6B6B66]">
                            {pctLabel(row[t.per] || 0)}
                          </span>
                        </td>
                      ))}
                      <td className="font-bold">{num(rt)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#1D1D1B] bg-[#F7F7F5] text-right font-bold">
                  <td className="text-left">Total geral</td>
                  <td />
                  {TIPOS.map((t, i) => (
                    <td key={t.label}>
                      {num(sums[i])}{" "}
                      <span className="inline-block w-[52px] text-xs font-semibold text-[#6B6B66]">
                        {pctLabel(pct(sums[i], total))}
                      </span>
                    </td>
                  ))}
                  <td>{num(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {/* Ranking + Relatórios */}
        <section className="mb-8 flex flex-wrap gap-4">
          <div
            className={`${card} flex min-w-0 flex-[2_1_560px] flex-col gap-4`}
          >
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">Ranking de clientes</h2>
              <span className="text-[13px] text-[#6B6B66]">
                Clientes com mais vistorias no período
              </span>
            </div>
            <ol className="flex flex-col">
              {ranking.map((c, i) => {
                const first = i === 0;
                return (
                  <li
                    key={c.cpf_cnpj}
                    className={`flex items-center gap-3.5 py-3 ${
                      first ? "" : "border-t border-[#EEEDE9]"
                    }`}
                  >
                    <span
                      className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-[10px] text-[13px] font-bold tabular-nums ${
                        first
                          ? "bg-[#E84E1B] text-white"
                          : "bg-[#F1F0EC] text-[#1D1D1B]"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold">
                          {c.nome_cliente}
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F0EC] px-2 py-px text-[11px] font-semibold text-[#4A4A46]">
                          <span
                            className="h-1.5 w-1.5 rounded-sm"
                            style={{ background: tipoColor(c.tipo) }}
                          />
                          {tipoLabel(c.tipo)}
                        </span>
                        <span className="font-mono text-xs text-[#6B6B66]">
                          {c.cpf_cnpj}
                        </span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-[#F1F0EC]">
                        <div
                          className={`h-full rounded-full ${
                            first ? "bg-[#E84E1B]" : "bg-[#1D1D1B]"
                          }`}
                          style={{
                            width: `${pct(c.quantidade_vistorias, topQtd)}%`,
                          }}
                        />
                      </div>
                    </div>
                    <span className="w-[110px] flex-none text-right text-sm tabular-nums">
                      <b className="font-bold">{num(c.quantidade_vistorias)}</b>{" "}
                      <span className="text-[#6B6B66]">vistorias</span>
                    </span>
                  </li>
                );
              })}
              {!ranking.length && (
                <li className="py-6 text-center text-sm text-[#6B6B66]">
                  Nenhum cliente no período.
                </li>
              )}
            </ol>
          </div>

          <div
            className={`${card} flex min-w-0 flex-[1_1_320px] flex-col gap-4 self-start`}
          >
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">Relatórios</h2>
              <span className="text-[13px] text-[#6B6B66]">
                Exportados com os filtros aplicados
              </span>
            </div>
            <button
              type="button"
              onClick={handleDownDoc}
              className="flex min-h-16 items-center gap-3.5 rounded-xl border border-[#DAD9D4] bg-white px-4 py-3 text-left hover:bg-[#FAFAF8]"
            >
              <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-[10px] bg-[#FDEDE6] text-[#B83A12]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                  <path d="M8 13h8M8 17h5" />
                </svg>
              </span>
              <span className="flex flex-1 flex-col gap-0.5">
                <span className="text-sm font-semibold">
                  Produção por Despachante
                </span>
                <span className="text-xs text-[#6B6B66]">Planilha XLSX</span>
              </span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4A4A46"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
            </button>
          </div>
        </section>
      </div>
    </LayoutTemplate>
  );
};
